/**
 * Checking a saved grid against a published result. Pure comparison: no scoring,
 * no confidence, nothing predictive.
 */
import type { GameConfig } from './games';
import type { Grid } from './draw';

export interface MatchResult {
  mainHits: number;
  bonusHits: number;
  /** Which numbers matched, for highlighting. */
  matchedMain: number[];
  matchedBonus: number[];
  /** True when every main and bonus number matched. */
  jackpot: boolean;
  /** Short French label, e.g. "3 numéros + 1 étoile". Empty when nothing matched. */
  label: string;
}

export function matchGrid(grid: Grid, result: Grid, g: GameConfig): MatchResult {
  const mainSet = new Set(result.main);
  const bonusSet = new Set(result.bonus);
  const matchedMain = grid.main.filter((x) => mainSet.has(x));
  const matchedBonus = grid.bonus.filter((x) => bonusSet.has(x));
  const mainHits = matchedMain.length;
  const bonusHits = matchedBonus.length;

  const parts: string[] = [];
  if (mainHits > 0) parts.push(`${mainHits} numéro${mainHits > 1 ? 's' : ''}`);
  if (bonusHits > 0) {
    const noun = bonusNoun(g, bonusHits);
    parts.push(`${bonusHits} ${noun}`);
  }

  return {
    mainHits,
    bonusHits,
    matchedMain,
    matchedBonus,
    jackpot: mainHits === g.k && bonusHits === g.bk,
    label: parts.join(' + '),
  };
}

function bonusNoun(g: GameConfig, count: number): string {
  switch (g.key) {
    case 'em':
      return count > 1 ? 'étoiles' : 'étoile';
    case 'loto':
      return 'n° Chance';
    case 'eurodreams':
      return 'n° Dream';
  }
}
